import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Creators as proceduresByStudentActions } from '../../../domain/useCases/procedures/getProceduresByStudent';
import { Creators as requestStatusActions } from '../../../domain/useCases/procedures/getStudentProcedureStatus';
import { Creators as ProceduresNotAutomatedActions } from '../../../domain/useCases/procedures/registerTicket/getProceduresNotAutomated';
import { Creators as procedureActions } from '../../../domain/useCases/procedures/getProcedureInformation';
import ProceduresView from './ProceduresView';
import constants from './constants';
import routes from '../../components/Routes/routingMap';
import XCheckpointManager from '../../../core/managers/xcheckpointManager';
import GoogleAnalytics from '@/presentation/hooks/useGoogleAnalytics';

export const handleSelectRequest = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const execute = (requestId: number, procedureId: number) => {
    let urlText = '';
    if (procedureId === 1039) {
      urlText = 'intercorp';
    } else if (procedureId === 1040) {
      urlText = 'otro';
    }

    dispatch(
      requestStatusActions.getStudentProcedureStatus(procedureId, requestId)
    );

    const routeMap: Record<number, string | ((id: number) => string)> = {
      [constants.categories.requests.procedures.updatePhoto]:
        routes.student.procedures.categories.requests.updatephoto
          .procedureprogress,
      [constants.categories.requests.procedures.removeOfPeriod]:
        routes.student.procedures.categories.requests.retirementperiod
          .procedureprogress,
      [constants.categories.requests.procedures.issuanceUniversityCard]:
        routes.student.procedures.categories.requests.issuancefuniversitycard
          .procedureprogress,
      [constants.categories.requests.procedures.changeCareerCampusModality]:
        routes.student.procedures.categories.requests.changecareercampusmodality
          .procedureprogress,
      [constants.categories.requests.procedureListTemp[0].procedureId]:
        routes.student.procedures.categories.requests.changeStudiesOption
          .procedureprogress,
      [constants.categories.scholarships.procedureList[0].procedureId]:
        routes.student.procedures.categories.scholarships.bse.progress,
      [constants.categories.requests.procedures.certificateOfStudies]:
        routes.student.procedures.categories.requests.certificateOfStudies
          .procedureprogress,
      [constants.categories.requests.procedures.reincorporation]:
        routes.student.procedures.categories.requests.reincorporation.progress,
      [constants.categories.requests.procedures.examStragglers]:
        routes.student.procedures.categories.requests.lateExamen.progress,
      [constants.categories.requests.procedures.retreatCourse]:
        routes.student.procedures.categories.requests.retreatCourse.progress,
      [constants.categories.requests.procedures.pse]:
        routes.student.procedures.categories.requests.pse.progress,
      [constants.categories.requests.procedures.employabilityCharter]:
        routes.student.procedures.categories.requests.employabilityCharter
          .progress,
      [constants.categories.requests.procedures.rectificationNotes]:
        routes.student.procedures.categories.requests.rectificationOfNotes
          .progress,
      [constants.categories.requests.procedures
        .scholarshipsDiscounts]: `/tramites/becas-descuentos/${urlText}/progreso`,
      [constants.categories.certificates.type1Ids[0]]: (id: number) => {
        const certificateFound =
          constants.categories.certificates.type1List.find(
            (p) => p.procedureId === id
          );
        return routes.student.procedures.categories.certificates.certificatetype01.procedureprogress.replace(
          ':certificatetype01name',
          certificateFound?.url || `${id}`
        );
      }
    };
    let route = routeMap[procedureId];
    if (!route) {
      route = routeMap['1018'];
    }
    if (typeof route === 'function') {
      history.push(route(procedureId));
    } else if (route) {
      history.push(route);
    }
  };

  return { execute };
};

const ProceduresViewController: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  // states
  const [selectedCategory, setSelectedCategory]: any = useState(0);
  const [searchText, setSearchText] = useState('');
  const [metadataFound, setMetadataFound] = React.useState<any[]>([]);
  const {
    isLoading: isLoadingDataProcedures,
    data: proceduresNoAutomatedList
  } = useSelector((state: any) => state.proceduresNotAutomated);

  const { data: myProceduresList, isLoading } = useSelector(
    (state: any) => state.proceduresByStudent
  );

  // effetcs
  useEffect(() => {
    XCheckpointManager.registerStart('Trámites');
    document.title = `UTP + Portal | Trámites`;
    dispatch(proceduresByStudentActions.getProceduresByStudent());

    if (!proceduresNoAutomatedList)
      dispatch(ProceduresNotAutomatedActions.getProceduresNotAutomated());
    GoogleAnalytics.writeLog('Menu-Trámites', 'Menu');
    return () => {
      document.title = `UTP+ Portal | Universidad Tecnológica del Perú`;
      dispatch(proceduresByStudentActions.getProceduresByStudentClear());
    };
  }, []);

  useEffect(() => {
    document.getElementById('inputSearch')?.focus();
    if (proceduresNoAutomatedList && myProceduresList) {
      XCheckpointManager.registerEnd('Trámites');
    }
  }, [proceduresNoAutomatedList, myProceduresList]);

  const gotoRegisterTicket = (id = '') => {
    history.push(`/tramites/ticketera/registro?v=${id}`);
  };

  useEffect(() => {
    if (selectedCategory === 4) {
      history.push(`/tramites/autogestionables`);
    }

    if (selectedCategory === 5) {
      gotoRegisterTicket();
    }

    if (selectedCategory === 3) {
      history.push(`/tramites/${constants.categories.scholarships.url}`);
    }
  }, [selectedCategory]);

  const handleSelectCategory = (category: any) => {
    setSelectedCategory(category);
  };

  const changeSearchText = (text: string) => {
    setSearchText(text);
    const tickets = proceduresNoAutomatedList?.map((t: any) => ({
      ...t,
      type: 'TICKET'
    }));
    const certificates = constants.categories.certificates.type1List.map(
      (a: any) => ({ ...a, id: a.procedureId, type: 'CERTIFICATES' })
    );
    const selfServices = constants.categories.requests.procedureListTemp.map(
      (a: any) => ({ ...a, id: a.procedureId, type: 'SELFSERVICE' })
    );
    const scholarships = constants.categories.scholarships.procedureList.map(
      (a: any) => ({ ...a, id: a.procedureId, type: 'SCHOLARSHIP' })
    );
    const newMetaData = [
      ...tickets,
      ...selfServices,
      ...scholarships,
      ...certificates
    ]?.filter(
      (m: any) =>
        m?.name?.toLowerCase().includes(text.toLowerCase()) ||
        m?.tags?.toLowerCase().includes(text.toLowerCase())
    );
    setMetadataFound(newMetaData);
  };

  const selectItemFound = (item: any) => {
    GoogleAnalytics.writeLog('portal-procedure-searcherProcedure', 'procedure');
    if (
      item?.procedureId ===
      constants.categories.requests.procedures.fullCycleDiscount
    ) {
      history.push(
        routes.student.procedures.categories.requests.fullCycleDiscount
      );
      return;
    }

    if (item.type !== 'TICKET')
      dispatch(procedureActions.getProcedureInformation(item.id));
    if (item.type === 'SELFSERVICE') {
      if (!item?.isTemp) {
        history.push(
          `/tramites/autogestionables/solicitud/${item.url}`
          // routes.student.procedures.categories.requests.changeStudiesOption
          //   .procedureinfo
        );
      }
    }

    if (item.type === 'CERTIFICATES') {
      if (
        constants.categories.certificates.type1List.find(
          (p) => p.procedureId === item.id
        )
      ) {
        const cetificateFound =
          constants.categories.certificates.type1List.find(
            (p) => p.procedureId === item.id
          );
        history.push(
          routes.student.procedures.categories.certificates.certificatetype01.procedureinfo.replace(
            ':certificatetype01name',
            cetificateFound?.url || item.id
          )
        );
      }
    }

    if (item.type === 'SCHOLARSHIP') {
      if (item.id === constants.categories.requests.procedures.bse) {
        history.push(
          routes.student.procedures.categories.scholarships.bse.home
        );
      }
    }

    if (item.type === 'TICKET') {
      gotoRegisterTicket(item.id);
    }
  };

  const goToSelectRequest = handleSelectRequest();

  return (
    <ProceduresView
      handleSelectCategory={handleSelectCategory}
      handleSelectRequest={goToSelectRequest.execute}
      myProceduresList={myProceduresList}
      categoryList={constants.categoryList}
      proceduresListIsLoading={isLoading}
      loadingDataNoAutomated={isLoadingDataProcedures}
      itemsSearch={metadataFound}
      searchText={searchText}
      setSearchText={changeSearchText}
      selectItemFound={selectItemFound}
      sectionHeader={constants.sectionHeader}
    />
  );
};
export default ProceduresViewController;
